import {
  all, takeLatest, takeEvery, put, call, select
} from 'redux-saga/effects'
import api from 'services/api'
import { t } from 'ab18n'
import { notification } from 'antd'

import errorsFormat from 'shared/errors-format'
import history from 'shared/history'
import { actions, types } from './actions'

const setNotification = (message, duration) => (
  notification.error({ message, duration })
)

function errorsFormatException(error, message, setErrors = null) {
  const { response: { data: { errors }, status } } = error

  if (status === 500) {
    setNotification(message)
  } else if (setErrors && status !== 403) {
    setErrors(errors)
  } else {
    errorsFormat(error, message)
  }
}

function* fetchAllSaga(action) {
  const {
    meta: {
      type,
      filter,
      after,
      setErrors,
    }
  } = action

  yield put(actions.setLoading(true))
  try {
    const response = yield call(
      api.get,
      `/admin/store/${type}${filter || ''}`,
    )
    yield put(
      actions.setData(
        type === 'payment-methods' ? 'paymentMethods' : type,
        type === 'orders' ? response : response.data
      )
    )
    if (after) {
      after()
    }
  } catch (error) {
    console.error(error)
    if (setErrors) {
      const { response: { data: { errors } } } = error
      setErrors(errors)
    }
    errorsFormat(error, t('general.notification.error'))
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* fetchUnitsSaga() {
  yield put(actions.setLoading(true))
  try {
    const response = yield call(api.get, 'admin/store/units')

    yield put(actions.setUnits(response.data))
  } catch (err) {
    console.error(err)
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* fetchDefaultersSaga() {
  yield put(actions.setLoading(true))
  try {
    const response = yield call(api.get, 'admin/store/defaulters')

    yield put(actions.setDefaulters(response))
  } catch (err) {
    console.error(err)
    errorsFormat(err, t('general.notification.error'), 2)
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* createDefaulterSaga(action) {
  const { payload } = action
  const { unit, note, type } = payload

  yield put(actions.setLoading(true))
  try {
    yield call(api.post, 'admin/store/defaulters', {
      unit,
      note,
    })
    history.push('/admin/store/finances')
    notification.success({ message: t('financial.notification.create.success'), duration: 4 })

    if (type === 'edit') {
      yield put(actions.editDefaulters(unit))
      yield put(actions.openModal())
    }
  } catch (err) {
    console.error(err)
    errorsFormat(err, t('general.notification.error'), 2)
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* patchDefaulterSaga(action) {
  const { payload, command } = action
  const {
    id, unit, isActive, updateResponsible, jumpCommand, note
  } = payload

  yield put(actions.setLoadingUpdate(true))

  try {
    yield call(api.put, 'admin/store/defaulters', {
      id, unit, isActive, jumpCommand, note, updateResponsible
    })
    if (!command) yield put(actions.openModal())
    yield put(actions.fetchDefaulters())
    notification.success({ message: t('admin.store.notification.kitMandatory.success'), duration: 4 })
  } catch (err) {
    console.error(err)
    errorsFormat(err, t('general.notification.error'), 2)
  } finally {
    yield put(actions.setLoadingUpdate(false))
  }
}

function* fetchWalletProductsAllSaga() {
  yield put(actions.setLoading(true))
  try {
    const response = yield call(api.get, 'admin/store/wallets/products',)
    yield put(actions.setWalletProducts(response.data))
  } catch (error) {
    console.error(error)
    errorsFormat(error, t('general.notification.error'), 2)
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* fetchProductsSaga() {
  yield put(actions.setLoading(true))
  try {
    const response = yield call(api.get, 'admin/store/products?notWallet=1',)
    yield put(actions.setProducts(response.data))
  } catch (error) {
    console.error(error)
    errorsFormat(error, t('general.notification.error'), 2)
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* createWalletProductSaga(action) {
  const { payload } = action
  yield put(actions.setLoading(true))
  try {
    yield call(api.post, 'admin/store/wallets/products', payload)
    notification.success({ message: t('wallets.notification.product.create.success'), duration: 2 })
    yield put(actions.fetchWalletProductsAll())
    yield put(actions.fetchProducts())
  } catch (error) {
    console.error(error)
    errorsFormat(error, t('wallets.notification.product.create.error'), 2)
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* postCouponSaga(action) {
  const { payload } = action
  yield put(actions.setLoading(true))
  try {
    yield call(api.post, 'admin/store/coupons', payload)
    notification.success({ message: t('coupon.notification.create.success'), duration: 2 })
    history.push('/admin/store/coupons')
  } catch (error) {
    console.error(error)
    errorsFormat(error, t('coupon.notification.create.error'), 2)
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* deleteCouponSaga(action) {
  const { payload } = action
  yield put(actions.setLoading(true))
  try {
    yield call(api.delete, `admin/store/coupons/${payload}`)
    notification.success({ message: t('coupon.notification.remove.success'), duration: 2 })
    yield put(actions.fetchAll({ type: 'coupons' }))
  } catch (error) {
    console.error(error)
    errorsFormat(error, t('coupon.notification.remove.error'), 2)
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* updateWalletProductSaga(action) {
  const { payload, meta } = action
  yield put(actions.setLoading(true))
  try {
    yield call(api.put, `admin/store/products/${payload}`, meta)
    notification.success({ message: t('wallets.notification.product.update.success'), duration: 2 })
  } catch (error) {
    console.error(error)
    errorsFormat(error, t('wallets.notification.product.update.error'), 2)
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* fetchOneSaga(action) {
  const { payload, meta: { type } } = action

  const nameSetData = action.meta.nameSetData ? action.meta.nameSetData : type

  yield put(actions.setLoading(true))
  try {
    const response = yield call(
      api.get,
      `/admin/store/${type}/${payload}`,
    )
    yield put(actions.setData(nameSetData, response))
  } catch (error) {
    console.error(error)
    errorsFormat(
      error,
      t('general.notification.error'),
      2,
      type === 'kits' ? '/admin/store' : `/admin/store/${type}`
    )
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* sendDataSaga(action) {
  const {
    payload,
    meta: {
      id,
      type,
      after,
      setErrors,
    }
  } = action

  yield put(actions.setLoading(true))
  try {
    if (id) {
      yield call(api.put, `/admin/store/${type}/${id}`, payload)
    } else {
      yield call(api.post, `/admin/store/${type}`, payload)
    }
    notification.success({
      message: t(
        `${
          type === 'promotions' || type === 'wallets'
            ? type
            : 'admin.store'
        }.notification.${id ? 'update' : 'create'}.success`
      ),
      duration: 2,
      onClose: () => after()
    })
  } catch (error) {
    console.error(error)
    errorsFormatException(
      error,
      t(
        `${
          type === 'promotions' || type === 'wallets'
            ? type
            : 'admin.store'
        }.notification.${id ? 'update' : 'create'}.error`
      ),
      setErrors
    )
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* generateLabelsSaga(action) {
  const { meta: { id } } = action

  yield put(actions.setLoading(true))
  try {
    const data = yield call(api.get, `/admin/store/tags/order/${id}`, {
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `pedido-${id}.prn`)
    document.body.appendChild(link)
    link.click()

    notification.success({
      message: t('admin.store.notification.labels.success'),
      duration: 2,
    })
  } catch (error) {
    console.error(error)
    errorsFormat(error, t('admin.store.notification.labels.error'))
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* sendPatchDataSaga(action) {
  const { payload, meta: { id, type, after } } = action

  yield put(actions.setLoading(true))
  try {
    yield call(api.patch, `/admin/store/${type}/${id}`, payload)
    notification.success({
      message: t(
        `admin.store.notification.${id ? 'update' : 'create'}.success`
      ),
      duration: 2,
      onClose: () => after()
    })
  } catch (error) {
    console.error(error)
    errorsFormat(
      error,
      t(
        `admin.store.notification.${id ? 'update' : 'create'}.error`
      )
    )
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* changeStatusOrderSaga(action) {
  const { meta: { id, status } } = action

  yield put(actions.setLoading(true))
  try {
    const response = yield call(api.post, `/admin/store/orders/${id}/${status}`)
    yield put(actions.setData('orders', response))
    notification.success({
      message: t(`admin.store.notification.order.${status}`),
      duration: 2,
    })
  } catch (error) {
    console.error(error)
    errorsFormat(
      error,
      t('admin.store.notification.order.errorCancel')
    )
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* sendOrderNfSaga(action) {
  const { payload, meta: { id } } = action

  yield put(actions.setLoading(true))
  try {
    const response = yield call(
      api.put,
      `/admin/store/orders/${id}`,
      payload,
    )
    yield put(actions.setData('orders', response))
    notification.success({
      message: t('admin.store.notification.order.nf'),
      duration: 2,
    })
  } catch (error) {
    console.error(error)
    errorsFormat(
      error,
      t('admin.store.notification.order.errorNf')
    )
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* sendEnableBasicKitRequiredSaga(item) {
  yield put(actions.setLoading(true))
  try {
    const response = yield call(api.post, `/admin/store/units/enableBasicKitRequired/${item.email}`)
    yield put(actions.setData('', response))
    notification.success({
      message: t('admin.store.notification.kitMandatory.success'),
      duration: 2,
    })
  } catch (error) {
    console.error(error)
    errorsFormat(
      error,
      t('admin.store.notification.kitMandatory.error')
    )
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* updatePurchaseLimitSaga(action) {
  const {
    email, purchaseLimit, purchaseLimitType, purchaseLimitPeriodStart, purchaseLimitPeriodEnd,
  } = action.payload

  yield put(actions.setSavingPurchaseLimit(email))
  try {
    const response = yield call(
      api.put,
      `admin/store/units/${encodeURIComponent(email)}/purchase-limit`,
      {
        purchaseLimit, purchaseLimitType, purchaseLimitPeriodStart, purchaseLimitPeriodEnd,
      },
    )
    yield put(actions.setUnit(response))
    notification.success({
      message: t('financial.purchaseLimit.notification.success'),
      duration: 2,
    })
  } catch (error) {
    console.error(error)
    errorsFormat(error, t('financial.purchaseLimit.notification.error'), 3)
  } finally {
    yield put(actions.setSavingPurchaseLimit(null))
  }
}

function* fetchOrdersProcessingSaga() {
  const current = yield select(({ admin }) => admin.ordersProcessing)
  yield put(actions.setOrdersProcessing(current, true))
  try {
    const response = yield call(api.get, 'admin/store/units/orders-processing')
    yield put(actions.setOrdersProcessing(response.data))
  } catch (error) {
    console.error(error)
    yield put(actions.setOrdersProcessing(current))
    errorsFormat(error, t('general.notification.error'), 3)
  }
}

function* updateAllowOrderWhileProcessingSaga(action) {
  const { email, allowOrderWhileProcessing } = action.payload

  // atualização otimista: a chave muda na hora e volta se a API falhar
  yield put(actions.setUnit({ email, allowOrderWhileProcessing }))
  yield put(actions.setSavingAllowOrder(email))
  try {
    const response = yield call(
      api.put,
      `admin/store/units/${encodeURIComponent(email)}/allow-order-while-processing`,
      { allowOrderWhileProcessing },
    )
    yield put(actions.setUnit(response))
    notification.success({
      message: t('financial.allowOrderWhileProcessing.notification.success'),
      duration: 2,
    })
  } catch (error) {
    console.error(error)
    yield put(actions.setUnit({ email, allowOrderWhileProcessing: !allowOrderWhileProcessing }))
    errorsFormat(error, t('financial.allowOrderWhileProcessing.notification.error'), 3)
  } finally {
    const saving = yield select(({ admin }) => admin.savingAllowOrder)
    if (saving === email) yield put(actions.setSavingAllowOrder(null))
  }
}

export default function* () {
  yield all([
    takeLatest(types.FETCH_ALL, fetchAllSaga),
    takeLatest(types.FETCH_WALLET_PRODUCTS_ALL, fetchWalletProductsAllSaga),
    takeLatest(types.FETCH_PRODUCTS, fetchProductsSaga),
    takeLatest(types.FETCH_UNITS, fetchUnitsSaga),
    takeLatest(types.FETCH_DEFAULTERS, fetchDefaultersSaga),
    takeLatest(types.CREATE_DEFAULTER, createDefaulterSaga),
    takeLatest(types.PATCH_DEFAULTER, patchDefaulterSaga),
    takeLatest(types.CREATE_WALLET_PRODUCT, createWalletProductSaga),
    takeLatest(types.UPDATE_WALLET_PRODUCT, updateWalletProductSaga),
    takeLatest(types.POST_COUPON, postCouponSaga),
    takeLatest(types.DELETE_COUPON, deleteCouponSaga),
    takeLatest(types.FETCH_ONE, fetchOneSaga),
    takeLatest(types.SEND_DATA, sendDataSaga),
    takeLatest(types.SEND_PATCH_DATA, sendPatchDataSaga),
    takeLatest(types.GENERATE_LABELS, generateLabelsSaga),
    takeLatest(types.CHANGE_STATUS_ORDER, changeStatusOrderSaga),
    takeLatest(types.SEND_ORDER_NF, sendOrderNfSaga),
    takeLatest(types.SEND_ENABLE_BASIC_KIT_REQUIRED, sendEnableBasicKitRequiredSaga),
    takeEvery(types.UPDATE_PURCHASE_LIMIT, updatePurchaseLimitSaga),
    takeEvery(types.UPDATE_ALLOW_ORDER_WHILE_PROCESSING, updateAllowOrderWhileProcessingSaga),
    takeLatest(types.FETCH_ORDERS_PROCESSING, fetchOrdersProcessingSaga),
  ])
}
